import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ComputedPageItem, IntColour} from '@taopix/taopix-core/dist/types/types';
import {BackgroundIcon, ContentPanel, RangeWithValue} from '@taopix/taopix-design-system';
import {BoxLayer, BoxType, BoxTypeShape, NULL_COLOUR_HEX, NumBool} from '@taopix/taopix-core';
import {useTaopix} from '../../../Context/TaopixContext';
import classNames from 'classnames';
import {useExperience} from '../../../Experience/ExperienceProvider';
import Colour from './Colour';
import {Tile} from '../../../BackgroundsList/BackgroundsList';
import {useUiState} from '../../../Context/UserInterface/UserInterfaceContext';
import {getSafeColour} from '../../../../Utils/Colour';
import useSettings from '../../../../Hooks/useSettings';
import ToolPaletteSection from '../../../ToolPalette/ToolPaletteSection';
import PaletteInformationMessage from '../PaletteInformationMessage';

export type BackgroundProps = {
  sectionKey: string;
  item?: ComputedPageItem;
};

type BackgroundExperienceValues = {
  backgroundColour: boolean;
  backgroundTransparency: boolean;
};

const Background = ({sectionKey, item}: BackgroundProps) => {
  const app = useTaopix();
  const settings = useSettings();
  const ui = useUiState();
  const {t} = useTranslation();

  const {experience} = useExperience();
  const {textBoxOptions, shapeBoxOptions} = experience.EDITOR;

  const [showSection, setShowSection] = useState<boolean>(false);
  const [disableSection, setDisableSection] = useState<boolean>(false);
  const [notAvailableMessage, setNotAvailableMessage] = useState<string>('');

  const [selectedBackgroundColour, setSelectedBackgroundColour] = useState<
    string | number | undefined
  >(getSafeColour(item?.backcolour as IntColour));

  const [shapeTransparency, setShapeTransparency] = useState<number>(item?.alpha as number);

  const [itemBackgroundOptions, setItemBackgroundOptions] = useState<BackgroundExperienceValues>({
    backgroundColour: true,
    backgroundTransparency: true,
  });

  const colours = settings.state?.colours?.palette;

  const isActiveInExperience = (item: ComputedPageItem) => {
    // Return whether or not this option is enabled in the experience for the selected item
    const boxType = item.type;
    if (boxType === BoxType.Text) {
      setItemBackgroundOptions({
        backgroundColour: textBoxOptions.backgroundColour,
        backgroundTransparency: textBoxOptions.backgroundTransparency,
      });
      return textBoxOptions.background;
    } else if (
      boxType === BoxType.Shape &&
      [BoxTypeShape.Oval, BoxTypeShape.Polygon, BoxTypeShape.Rect].includes(item?.shape)
    ) {
      setItemBackgroundOptions({
        backgroundColour: shapeBoxOptions.backgroundColour,
        backgroundTransparency: shapeBoxOptions.backgroundTransparency,
      });
      return shapeBoxOptions.background;
    } else {
      return false;
    }
  };

  // Check if we can show this option based on the selected item type, its protection, the experience and canEditPageLayout
  useEffect(() => {
    if (item && item.ref !== -1) {
      // If there is a selected item
      if (isActiveInExperience(item)) {
        // Show the button
        setShowSection(true);
        // Set whether the button should be disabled or not
        setDisableSection(
          NumBool.True === item.protectcontents || (item.errors?.imageMissing as boolean)
        );
      } else {
        // If this palette is not enabled for the selected item, hide the toolpalette button and show the 'not available for the selected item' message
        setShowSection(false);
        setNotAvailableMessage(t('toolpalette:str_OptionNotAvailableForSelectedItem'));
      }
    } else {
      // If no item is selected, hide the button and show the 'please select an item' message
      setShowSection(false);
      setNotAvailableMessage(t('toolpalette:str_MessageSelectAnItemToViewOptions'));
    }
  }, [item?.asset, item?.asset?.assetType, item?.type, item?.image, item?.shape]);

  useEffect(() => {
    const colour = getSafeColour(item?.backcolour as IntColour);
    if (colour !== '') {
      setSelectedBackgroundColour(colour.replace('#', ''));
    } else {
      setSelectedBackgroundColour(NULL_COLOUR_HEX);
    }
  }, [item?.ref]);

  useEffect(() => {
    if (
      ui.state.palette.section !== 'box-settings-palette/background' &&
      ui.state.palette.section !== 'box-settings-palette/background/background-colour'
    ) {
      return;
    }
    if (selectedBackgroundColour !== '' || selectedBackgroundColour !== undefined) {
      if (
        item?.ref !== -1 &&
        (item?.layer === BoxLayer.Background || item?.layer === BoxLayer.Normal)
      ) {
        if (selectedBackgroundColour === NULL_COLOUR_HEX) {
          app.core.dispatch('SHAPE.SET_BACK_COLOUR', `${selectedBackgroundColour}`).catch(error => {
            console.error(error);
          });
        } else {
          app.core
            .dispatch('SHAPE.SET_BACK_COLOUR', `#${selectedBackgroundColour}`)
            .catch(error => {
              console.error(error);
            });
        }
      }
    }
  }, [selectedBackgroundColour]);

  useEffect(() => {
    if (item?.alpha) {
      setShapeTransparency(item?.alpha);
    }
  }, [item?.alpha]);

  const sectionButtonClasses = classNames('lg:py-md');

  const wrapperClasses = classNames(
    'flex',
    'flex-col',
    'px-md',
    'space-y-sm',
    'lg:flex-1',
    'sm:pb-md',
    'sm:h-mobilePaletteContentHeight',
    'sm:max-w-full',
    'overflow-y-auto'
  );

  if (
    ui.state.palette.section !== 'box-settings-palette' &&
    ui.state.palette.section !== `box-settings-palette/${sectionKey}` &&
    ui.state.palette.section !== `box-settings-palette/${sectionKey}/background-colour` &&
    ui.state.palette.section !== `box-settings-palette/${sectionKey}/transparency`
  ) {
    return null;
  }

  return (
    <ToolPaletteSection
      id={'boxSettingsBackgroundPalette'}
      title={t('boxpalettes:str_TitleBackground')}
      buttonIcon={<BackgroundIcon />}
      sectionKey={sectionKey}
      buttonClasses={sectionButtonClasses}
      useFocusMode
      subSection={true}
      disabled={disableSection}
      showButton={showSection}
      buttonLayout="horizontal"
      parentselected={true}
    >
      <div className={wrapperClasses}>
        {!showSection || disableSection ? (
          // If the palette is not allowed by the experience, or is disabled by the box protection, show the relevant message
          // We show this message rather than closing/hiding the palette to avoid abrupt UI changes when no item or an invalid item is selected
          <PaletteInformationMessage message={notAvailableMessage} />
        ) : (
          <>
            {ui.state.palette.section?.includes('background') &&
              itemBackgroundOptions.backgroundColour && (
                <ContentPanel>
                  <Colour
                    id={'box-settings-background-colour-picker'}
                    data-testid={'box-settings-background-colour-picker'}
                    parentSection={'item-background'}
                    sectionKey={'background-colour'}
                    buttonLabel={t('*:str_LabelColour')}
                    sectionTitle={t('boxpalettes:str_LabelBackgroundColour')}
                    item={item}
                    colours={colours}
                    selectedColour={selectedBackgroundColour}
                    allowTransparent={item?.type === BoxType.Text}
                    onClick={(
                      event: React.MouseEvent | React.ChangeEvent,
                      newSelectedBackground: Tile
                    ) => {
                      if (selectedBackgroundColour === newSelectedBackground?.src) {
                        setSelectedBackgroundColour(NULL_COLOUR_HEX);
                      } else {
                        setSelectedBackgroundColour(newSelectedBackground?.src);
                      }
                    }}
                    useFocusMode
                  />
                </ContentPanel>
              )}
            {itemBackgroundOptions.backgroundTransparency && (
              <ContentPanel>
                <RangeWithValue
                  data-testid={'box-settings-background-transparency-slider'}
                  key={`background-transparency-${item?.ref}`}
                  label={t('str_TitleTransparency', {ns: 'boxpalettes'})}
                  value={shapeTransparency}
                  id={'shape-transparency-slider'}
                  min={10}
                  max={100}
                  submitOnBlur={true}
                  onValueChange={(value: number, interacting?: boolean) => {
                    if (item?.ref && item?.ref !== -1) {
                      app.core.dispatch('SHAPE.UPDATE_OPACITY', value, interacting).catch(error => {
                        console.error(error);
                      });
                    }
                  }}
                  isSmallScreen={ui.state.isSmallScreen}
                />
              </ContentPanel>
            )}
          </>
        )}
      </div>
    </ToolPaletteSection>
  );
};

export default Background;
